
import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

// Critical imports (Eager loading for LCP/FCP)
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import ScrollToAnchor from '@/components/ScrollToAnchor';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';

// Consolidate smaller chunks to reduce HTTP requests and break critical chains
// These components are small enough to be bundled with the main chunk or a shared chunk
// rather than incurring the overhead of separate lazy chunks.
import HowItWorks from '@/components/HowItWorks';
import FAQ from '@/components/FAQ';
import ContactForm from '@/components/ContactForm';

// Keep heavy components lazy loaded
const ComparisonTable = React.lazy(() => import('@/components/ComparisonTable'));
const AlternativesGrid = React.lazy(() => import('@/components/AlternativesGrid'));
const Footer = React.lazy(() => import('@/components/Footer'));
const Toaster = React.lazy(() => import('@/components/ui/toaster').then(module => ({ default: module.Toaster })));
const ResourcesPage = React.lazy(() => import('@/pages/ResourcesPage'));
const ArticlePage = React.lazy(() => import('@/pages/ArticlePage'));

function App() {
  // Enhanced Structured Data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://directautoreports.com/#website",
        "url": "https://directautoreports.com",
        "name": "Direct Auto Reports",
        "description": "Instant CARFAX & Vehicle History Reports",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://directautoreports.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "Organization",
        "@id": "https://directautoreports.com/#organization",
        "name": "Direct Auto Reports",
        "url": "https://directautoreports.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://directautoreports.com/logo.png"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "support@directautoreports.com",
          "contactType": "customer support"
        }
      },
      {
        "@type": "Product",
        "name": "CARFAX Vehicle History Report",
        "description": "Comprehensive vehicle history report including accidents, ownership history, service records, and recalls.",
        "brand": {
          "@type": "Brand",
          "name": "CARFAX"
        },
        "offers": {
          "@type": "Offer",
          "price": "34.99",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        }
      }
    ]
  };

  // Specialized Loading Skeletons to prevent CLS
  const GridSkeleton = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex flex-col items-center mb-12 space-y-4">
        <Skeleton className="h-10 w-3/4 md:w-1/2" />
        <Skeleton className="h-6 w-2/3 md:w-1/3" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="p-6 border rounded-lg space-y-4">
            <div className="flex justify-between">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-6 w-16" />
            </div>
            <Skeleton className="h-8 w-1/4" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-10 w-full mt-4" />
          </div>
        ))}
      </div>
    </div>
  );

  const SimpleSectionSkeleton = () => (
    <div className="py-16 px-4 max-w-7xl mx-auto">
       <Skeleton className="h-10 w-1/2 mx-auto mb-8" />
       <div className="space-y-4 max-w-3xl mx-auto">
         <Skeleton className="h-24 w-full" />
         <Skeleton className="h-24 w-full" />
         <Skeleton className="h-24 w-full" />
       </div>
    </div>
  );

  const PageLoader = () => (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="h-10 w-10 animate-spin text-[#FF6B35]" />
    </div>
  );

  return (
    <BrowserRouter>
      <Helmet>
        <html lang="en" />
        <title>Direct Auto Reports | Instant CARFAX & Vehicle History Reports</title>
        <meta name="description" content="Get instant CARFAX vehicle history reports delivered to your email in minutes. Compare prices and find the best VIN check alternatives." />
        <link rel="canonical" href="https://directautoreports.com/" />
        
        {/* Content Security Policy */}
        <meta 
          http-equiv="Content-Security-Policy" 
          content="default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://js.stripe.com https://www.googletagmanager.com; connect-src 'self' https://analytics.google.com https://stats.g.doubleclick.net https://*.supabase.co wss://*.supabase.co; img-src 'self' data: https: blob:; style-src 'self' 'unsafe-inline'; font-src 'self' data:;" 
        />

        <meta http-equiv="Cache-Control" content="max-age=31536000" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://directautoreports.com/" />
        <meta property="og:title" content="Direct Auto Reports | Instant CARFAX Vehicle History" />
        <meta property="og:description" content="Get instant CARFAX vehicle history reports delivered to your email. Compare prices and features of top VIN check services." />
        <meta property="og:image" content="https://directautoreports.com/og-image.jpg" />
        <meta property="og:site_name" content="Direct Auto Reports" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://directautoreports.com/" />
        <meta property="twitter:title" content="Direct Auto Reports | Instant CARFAX Vehicle History" />
        <meta property="twitter:description" content="Get instant CARFAX vehicle history reports delivered to your email. Compare prices and features of top VIN check services." />
        <meta property="twitter:image" content="https://directautoreports.com/twitter-image.jpg" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>
      
      <div className="min-h-screen bg-white flex flex-col font-sans text-gray-900">
        <ScrollToAnchor />
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-white focus:text-blue-600">
          Skip to main content
        </a>

        <Navbar />
        
        <main id="main-content" className="flex-grow flex flex-col w-full">
          <Routes>
            <Route path="/" element={
              <>
                <section aria-label="Hero Section">
                  <Hero />
                </section>
                
                <section aria-label="Comparison Table" id="compare">
                  <Suspense fallback={<SimpleSectionSkeleton />}>
                    <ComparisonTable />
                  </Suspense>
                </section>
                
                <Suspense fallback={<GridSkeleton />}>
                  <section aria-label="Alternative Services" id="services">
                    <AlternativesGrid />
                  </section>
                </Suspense>
                
                <section aria-label="How It Works" id="how-it-works">
                  <HowItWorks />
                </section>
                
                <section aria-label="Frequently Asked Questions" id="faq">
                  <FAQ />
                </section>
                
                <section aria-label="Contact Us" id="contact">
                  <ContactForm />
                </section>
              </>
            } />
            <Route path="/resources" element={
              <Suspense fallback={<PageLoader />}>
                <ResourcesPage />
              </Suspense>
            } />
            <Route path="/resources/:slug" element={
              <Suspense fallback={<PageLoader />}>
                <ArticlePage />
              </Suspense>
            } />
          </Routes>
        </main>

        <Suspense fallback={<div className="h-20 bg-gray-50" />}>
          <Footer />
        </Suspense>
        
        <Suspense fallback={null}>
          <Toaster />
        </Suspense>
      </div>
    </BrowserRouter>
  );
}

export default App;
